
import { defineComponent } from 'vue'
import { format, parseISO, startOfMonth, endOfToday, differenceInDays } from 'date-fns'
import ptBR from 'date-fns/locale/pt-BR'
import { Table, PrimaryButton, Input, Form, Card, Select } from '@design-system/ui'
import { faPaperclip, faPaperPlane, faSearch, faTimesCircle, faTrash, faFileAlt, faMailBulk, faBrazilianRealSign, faCircleCheck, faShop, faFileInvoiceDollar, faSackDollar, faQuestionCircle } from '@fortawesome/free-solid-svg-icons'
import SessionService from '../SessionService'
import CustomInputAutocomplete from '../components/input/InputAutoComplete.vue'
import api from '@/services/api'
import { ListSoftwareHousesDTO } from '@/interfaces/ListSoftwareHousesDTO'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import Toast from '@/components/Toast.vue'

export default defineComponent({
  name: 'Volumetria',
  components: {
    Table,
    Select,
    PrimaryButton,
    Input,
    CustomInputAutocomplete,
    Form,
    FontAwesomeIcon,
    Card,
    Toast
  },
  data () {
    return {
      errorMessage: null,
      month: [
        'Janeiro',
        'Fevereiro',
        'Março',
        'Abril',
        'Maio',
        'Junho',
        'Julho',
        'Agosto',
        'Setembro',
        'Outubro',
        'Novembro',
        'Dezembro'
      ],
      reportTypeOptions: [
        {
          value: 'Simplificado',
          key: 'report-summary'
        },
        {
          value: 'Detalhado',
          key: 'report-detailed'
        }
      ],
      reportTypeSelected: {
        value: 'Simplificado',
        key: 'report-summary'
      },
      buttonIcons: {
        search: {
          icon: faSearch
        },
        sendEmail: {
          icon: faPaperPlane
        },
        cancel: {
          icon: faTimesCircle
        },
        trash: {
          icon: faTrash
        },
        upload: {
          icon: faPaperclip
        },
        file: {
          icon: faFileAlt
        },
        message: {
          icon: faMailBulk
        },
        done: {
          icon: faCircleCheck
        }
      },
      loading: false,
      loadingSoftwareHouses: false,
      message: '',
      isRoot: false,
      allSoftwareHouses: [],
      softwareHouseSelected: null,
      softwareHouses: [],
      isSoftwareHouseIncorrect: false,
      searchSoftwareHouseText: '',
      hidden: true,
      toastSuccess: false,
      reportModal: false,
      estabelecimentosQuantity: 0,
      transactionsQuantity: 0,
      loadingExport: false,
      revenueValue: 0,
      expenseValue: 0,
      balanceValue: 0,
      firstLoading: true,
      SOFTWARE_HOUSE_CNPJ: null,
      ptBRDate: ptBR,
      softwareHouse: {},
      startFilter: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
      endFilter: format(endOfToday(), 'yyyy-MM-dd'),
      userType: null,
      selectedValue: '',
      searchEstabelecimentoText: '',
      estabelecimentosAutoComplete: [],
      estabelecimentos: [],
      allEstabelecimentos: [],
      cnpjReport: '',
      cnpjCompany: null,
      cnpjSoftwareHouse: '',
      shopIcon: faShop,
      fileDollarIcon: faFileInvoiceDollar,
      sackDollarIcon: faSackDollar,
      brazilianRealSign: faBrazilianRealSign,
      questionCircleIcon: faQuestionCircle,
      timeSeen: 20000
    }
  },
  async mounted () {
    const session = new SessionService()
    const token = localStorage.getItem('token')

    if (session.rootAccess()) {
      this.isRoot = true
      this.listSoftwareHouses()
      this.firstLoading = false

      return
    }

    this.SOFTWARE_HOUSE_CNPJ = session.cnpj()
    this.cnpjSoftwareHouse = this.SOFTWARE_HOUSE_CNPJ
    this.cnpjCompany = ''
    this.softwareHouse = await session.softwareHouseData(token, this.SOFTWARE_HOUSE_CNPJ)
    const actualMonth = new Date().getMonth()
    this.currentMonth = actualMonth
    this.firstLoading = true
    this.getData()
    this.firstLoading = false
  },
  methods: {
    getData (value) {
      if (value) {
        this.updateSearchSoftwareHouseText(value.primaryText)
      }
      this.loading = true
      this.firstLoading = false

      const rangeValidated = this.validateDateRange(this.startFilter, this.endFilter)
      if (!rangeValidated) {
        this.loading = false
        return
      }

      this.faturamentoAtual = 0

      const queries = {
        start: this.startFilter,
        end: this.endFilter
      }

      api.get(`/volumetry/${this.softwareHouseSelected ? this.softwareHouseSelected.id : this.SOFTWARE_HOUSE_CNPJ}/summary`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        params: queries
      })
        .then(async response => {
          let transactionsQuantity = 0
          let revenueValue = 0
          let expenseValue = 0

          this.allEstabelecimentos = response.data.map(({ estabelecimentoComercialCnpj, estabelecimentoRazaoSocial, softwareHouseRazaoSocial, taxaMediacaoAtual, taxaNegociadaAtual, volumetria }) => {
            let quantidadeTotal = 0
            let valorTotalTaxaShTotal = 0
            let valorTotalTaxaCompanyTotal = 0
            volumetria.forEach((data) => {
              const { quantidade, valorTotalTaxaSh, valorTotalTaxaCompany } = data
              quantidadeTotal += quantidade
              valorTotalTaxaShTotal += parseFloat(valorTotalTaxaSh)
              valorTotalTaxaCompanyTotal += parseFloat(valorTotalTaxaCompany)
            })

            transactionsQuantity += quantidadeTotal
            revenueValue += Number(valorTotalTaxaCompanyTotal)
            expenseValue += Number(valorTotalTaxaShTotal)

            return {
              estabelecimentoComercialCnpj,
              estabelecimentoRazaoSocial,
              softwareHouseRazaoSocial,
              taxaMediacao: taxaMediacaoAtual,
              taxaNegociada: taxaNegociadaAtual,
              quantidade: quantidadeTotal,
              valorTotalTaxaSh: valorTotalTaxaShTotal,
              valorTotalTaxaCompany: valorTotalTaxaCompanyTotal
            }
          })

          this.transactionsQuantity = transactionsQuantity
          this.revenueValue = revenueValue
          this.expenseValue = expenseValue
          this.balanceValue = revenueValue - expenseValue
          this.estabelecimentosQuantity = this.allEstabelecimentos.length

          this.estabelecimentos = this.allEstabelecimentos
          this.estabelecimentosAutoComplete = this.searchEstabelecimentos()

          setTimeout(() => {
            var evt = new CustomEvent('list')
            window.dispatchEvent(evt)
          }, 1)

          this.loading = false
        })
        .catch(() => {
          this.comissaoLoading = false
          this.loading = false
        })
    },
    formatCnpj (cnpj: string): string {
      const cnpjFormatado = `${cnpj.slice(0, 2)}.${cnpj.slice(2, 5)}.${cnpj.slice(5, 8)}/${cnpj.slice(8, 12)}-${cnpj.slice(12)}`
      return cnpjFormatado
    },
    generateReport () {
      const queries = {
        start: this.startFilter,
        end: this.endFilter
      }

      const url = this.cnpjCompany
        ? `/volumetry/${this.cnpjSoftwareHouse}/${this.reportTypeSelected.key}/${this.cnpjCompany}`
        : `/volumetry/${this.cnpjSoftwareHouse}/${this.reportTypeSelected.key}`

      this.loadingExport = true
      api.get(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        params: queries,
        responseType: 'blob'
      })
        .then(response => {
          const objectUrl = window.URL.createObjectURL(new Blob([response.data]))

          const link = document.createElement('a')
          link.href = objectUrl
          this.reportModal = false
          link.setAttribute('download', 'report-tecnopay.xlsx')
          document.body.appendChild(link)
          link.click()

          setTimeout(() => {
            var evt = new CustomEvent('list')
            window.dispatchEvent(evt)
          }, 1)
          this.loadingExport = false
        })
        .catch(() => {
          this.loading = false
          this.hidden = false
          this.toastSuccess = false
          this.message = 'Ocorreu um erro ao gerar o relatório.'
          setTimeout(() => {
            this.hidden = true
          }, this.timeSeen)
        })
    },
    handleEstabelecimento (estabelecimento) {
      this.loading = true

      if (!estabelecimento) {
        this.cnpjCompany = null

        let transactionsQuantity = 0
        let revenueValue = 0
        let expenseValue = 0

        this.allEstabelecimentos.forEach(({ quantidade, valorTotalTaxaSh, valorTotalTaxaCompany }) => {
          transactionsQuantity += quantidade
          revenueValue += Number(valorTotalTaxaCompany)
          expenseValue += Number(valorTotalTaxaSh)
        })

        this.transactionsQuantity = transactionsQuantity
        this.revenueValue = revenueValue
        this.expenseValue = expenseValue
        this.balanceValue = revenueValue - expenseValue
        this.estabelecimentosQuantity = this.allEstabelecimentos.length
        this.estabelecimentos = this.allEstabelecimentos
      } else {
        this.updateSearchEstabelecimentoText(estabelecimento.primaryText)
        const filteredEstabelecimento = this.allEstabelecimentos.find((estabelecimentoData) => {
          return estabelecimentoData.estabelecimentoComercialCnpj === estabelecimento.id
        })

        this.transactionsQuantity = filteredEstabelecimento.quantidade
        this.revenueValue = Number(filteredEstabelecimento.valorTotalTaxaCompany)
        this.expenseValue = Number(filteredEstabelecimento.valorTotalTaxaSh)
        this.balanceValue = this.revenueValue - this.expenseValue
        this.estabelecimentosQuantity = 1
        this.estabelecimentos = [filteredEstabelecimento]
        this.cnpjCompany = filteredEstabelecimento.estabelecimentoComercialCnpj
      }

      setTimeout(() => {
        this.loading = false
      }, 250)
    },

    updateSearchEstabelecimentoText (value) {
      this.searchEstabelecimentoText = value
      this.estabelecimentosAutoComplete = this.searchEstabelecimentos()
      if (!value) {
        this.handleEstabelecimento()
      }
    },

    searchEstabelecimentos () {
      let estabelecimentos = this.allEstabelecimentos

      if (!this.searchEstabelecimentoText) {
        return estabelecimentos
      }

      if (this.searchEstabelecimentoText) {
        const estabelecimentoCnpj = this.searchEstabelecimentoText.replaceAll('/', '')
          .replaceAll('.', '')
          .replaceAll('-', '')
        estabelecimentos = estabelecimentos.filter((estabelecimento) => {
          const cnpjWithoutMask = estabelecimento.estabelecimentoComercialCnpj.replaceAll('/', '')
            .replaceAll('.', '')
            .replaceAll('-', '')
          return estabelecimento.estabelecimentoRazaoSocial.toLowerCase().includes(this.searchEstabelecimentoText.toLowerCase()) ||
            cnpjWithoutMask.includes(estabelecimentoCnpj)
        })
      }

      return estabelecimentos
    },

    async listSoftwareHouses () {
      this.loadingSoftwareHouses = true
      api.get<ListSoftwareHousesDTO[]>('/software-houses', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      })
        .then(response => {
          this.allSoftwareHouses = response.data
          this.softwareHouses = this.allSoftwareHouses

          this.loadingSoftwareHouses = false

          setTimeout(() => {
            var evt = new CustomEvent('list')
            window.dispatchEvent(evt)
          }, 1)
        })
        .catch(() => {
          this.loadingSoftwareHouses = false
        })
    },

    searchSoftwareHouses () {
      let softwareHouses = this.allSoftwareHouses

      if (!this.searchSoftwareHouseText) {
        return softwareHouses
      }

      if (this.searchSoftwareHouseText) {
        const softwareHouseCnpj = this.searchSoftwareHouseText.replaceAll('/', '')
          .replaceAll('.', '')
          .replaceAll('-', '')
        softwareHouses = softwareHouses.filter((softwareHouse) => {
          const cnpjWithoutMask = softwareHouse.cnpj.replaceAll('/', '')
            .replaceAll('.', '')
            .replaceAll('-', '')
          return softwareHouse.razaoSocial.toLowerCase().includes(this.searchSoftwareHouseText) ||
            softwareHouse.nomeFantasia.toLowerCase().includes(this.searchSoftwareHouseText.toLowerCase()) ||
            cnpjWithoutMask.includes(softwareHouseCnpj)
        })
      }

      return softwareHouses
    },

    async selectSoftwareHouse (value) {
      const session = new SessionService()
      const token = localStorage.getItem('token')

      try {
        this.softwareHouse = await session.softwareHouseData(token, value.id)
      } catch (error) {
        if (error.response && error.response.status === 404) {
          this.softwareHouseSelected = null
          this.hidden = false
          this.toastSuccess = false
          this.message = 'Software house não está cadastrada no SHOP!'
          setTimeout(() => {
            this.hidden = true
          }, this.timeSeen)
          return
        }
      }

      this.softwareHouseSelected = value
      this.cnpjSoftwareHouse = value.id
      await this.getData(value)
    },

    updateSearchSoftwareHouseText (value) {
      this.searchSoftwareHouseText = value
      this.softwareHouses = this.searchSoftwareHouses()
    },

    validateDateRange (startDateStr: string, endDateStr: string) {
      const intervalDays = differenceInDays(new Date(endDateStr), new Date(startDateStr))
      const maxInterval = 90

      if (new Date(endDateStr) > new Date()) {
        this.errorMessage = 'Não é possível buscar dados futuros'
        return false
      }

      if (intervalDays > maxInterval) {
        this.errorMessage = 'Intervalo de datas maior que 90 dias'
        return false
      }

      this.errorMessage = ''
      return true
    },
    recovery () {
      this.loading = true

      this.loading = false
    },
    resetFile () {
      this.$refs.imageUploader.value = ''
    },
    handleReportType (selected) {
      this.reportTypeSelected = selected
    },
    hide () {
      this.hidden = true
    },
    formatDate: format,
    parseISODate: parseISO
  },
  beforeMount () {
    this.$store.commit('changeActiveMenu', 3)
  },
  convertToBRL (decimalNumber) {
    const formattedBRL = new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(decimalNumber)

    return formattedBRL
  }
})
